import React, { useState } from 'react';
import ArrowDown from '../Icons/ArrowDown';
import ArrowUp from '../Icons/ArrowUp';
import LinkIcon from '../Icons/Link';
import { useRouter } from 'next/router';
import LoadingIcon from '../Icons/LoadingIcon';
import { Log } from '@/logging/datadog';

interface TicketProps {
  ticketInfo: {
    articleNumber: string;
    categoryGroups: {
      groupLabel: string;
      groupName: string;
      selectedCategories: {
        categoryLabel: string;
        categoryName: string;
        url: string;
      }[];
    }[];
    downVoteCount: number;
    id: string;
    lastPublishedDate: string;
    nextReviewDate: string | null;
    summary: string;
    title: string;
    upVoteCount: number;
    url: string;
    urlName: string;
    viewCount: number;
    viewScore: number;
  };
  ticketOpenURL: boolean;
}

const Ticket: React.FC<TicketProps> = ({ ticketInfo, ticketOpenURL }) => {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  interface TicketDetails {
    // Define the structure of the details object here
    summary: string;
    layoutItems: Array<{ label: string; name: string; type: string; value: string }>;
    // Add more fields as needed
  }

  const [details, setDetails] = useState<TicketDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetails = async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const credentials = process.env.NEXT_PUBLIC_SPURT_SVARAD_CREDENTIALS;
      const response = await fetch(`${process.env.NEXT_PUBLIC_SPURT_SVARAD_ARTICLE_ENDPOINT}${id}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${credentials}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch details');
      const data = await response.json();
      setDetails(data);
    } catch (error) {
      setError('An error occurred while fetching details.');
      Log.error('Error in fetchDetails', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDetails = () => {
    if (!isOpen) {
      fetchDetails(ticketInfo.id);
    }
    if (ticketOpenURL) {
      const { pathname, query } = router;
      delete query.id;
      router.replace({ pathname, query }, undefined, { shallow: true });
    }
    setIsOpen(!isOpen);
  };

  if (ticketOpenURL && !isOpen) {
    toggleDetails();
  }

  const layoutItemsRichText = details?.layoutItems.find(
    (item) => item.type === 'RICH_TEXT_AREA' && item.label === 'Article'
  );

  const copyToClipboard = () => {
    const domain = window.location.origin; // Get the domain (protocol + host)
    const route = window.location.pathname; // Get the current route
    const _link = `${domain}${route}?id=${ticketInfo.id}`;
    navigator.clipboard
      .writeText(_link)
      .then(() => {
        Log.info('Text copied to clipboard');
      })
      .catch((err) => {
        Log.error('Failed to copy text: ', err);
      });
  };

  return (
    <div
      className="relative"
      style={{
        marginTop: '1rem',
        marginBottom: '1rem',
        backgroundColor: 'white',
        borderRadius: '8px',
        ...(isOpen ? { boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.24)' } : { border: '1px solid #D2D2D3' }),
      }}
    >
      {isOpen && (
        <div className="absolute cursor-pointer" onClick={copyToClipboard} style={{ left: '-36px', top: '16px' }}>
          <LinkIcon />
        </div>
      )}
      <div
        className="cursor-pointer"
        onClick={toggleDetails}
        style={{
          paddingLeft: '32px',
          paddingRight: '32px',
          paddingTop: '16px',
          color: '#4A4D4E',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          ...(isOpen ? { marginBottom: '2rem' } : { paddingBottom: '16px' }),
        }}
      >
        <h2 style={{ fontSize: '18px', fontWeight: '600' }}>{ticketInfo.title}</h2>
        {isOpen ? <ArrowUp /> : <ArrowDown />}
      </div>
      {isOpen && (
        <div style={{ paddingLeft: '32px', paddingRight: '32px', paddingBottom: '16px' }}>
          {isLoading ? (
            <div className="flex items-center justify-center">
              <LoadingIcon />
            </div>
          ) : error ? (
            <p>{error}</p>
          ) : (
            details && (
              <div style={{ color: '#777A7A' }}>
                {/* Render details from the fetched data */}
                <div
                  className="sos-ticket-container"
                  dangerouslySetInnerHTML={{ __html: layoutItemsRichText?.value ?? '' }}
                ></div>
                {/* You can add more fields from the response here */}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default Ticket;
